// ═══════════════════════════════════════════════════════════════
// PersonEditModal.js — Création / édition d'une personne (Family P2c)
// ═══════════════════════════════════════════════════════════════
// Modal dark-mode-aware. Création si `person` null, édition sinon.
// Champs : full_name, role, birth_date, nationalities (CSV).
// Promotion admin2 via action dédiée (pas dans ce modal).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { X, Save, Loader2, Trash2 } from 'lucide-react';
import { createPerson, updatePerson, deletePerson } from '../../services/familyApi';
import { useTheme } from '../../contexts/ThemeContext';
import { toast, confirm } from '../ui/Toast';

const PersonEditModal = ({ projectId, person, open, onClose, onSaved, onDeleted }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const isEdit = !!person;

    const [fullName, setFullName] = useState('');
    const [role, setRole] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [nationalities, setNationalities] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setFullName(person?.full_name || '');
        setRole(person?.role || '');
        setBirthDate(person?.birth_date ? person.birth_date.slice(0, 10) : '');
        setNationalities((person?.nationalities || []).join(', '));
    }, [open, person]);

    const handleSave = async () => {
        if (!fullName.trim()) {
            toast.error('Nom complet requis');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                full_name: fullName.trim(),
                role: role.trim() || null,
                birth_date: birthDate || null,
                nationalities: nationalities
                    .split(',')
                    .map((s) => s.trim().toUpperCase())
                    .filter(Boolean)
                    .slice(0, 10),
            };
            const result = isEdit
                ? await updatePerson(projectId, person.id, payload)
                : await createPerson(projectId, payload);
            toast.success(isEdit ? 'Personne mise à jour' : 'Personne créée');
            onSaved?.(result);
            onClose?.();
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEdit) return;
        const ok = await confirm(
            `Supprimer « ${person.full_name} » de la famille ?`,
            { danger: true, confirmText: 'Supprimer' }
        );
        if (!ok) return;
        setSaving(true);
        try {
            await deletePerson(projectId, person.id);
            toast.success('Personne retirée');
            onDeleted?.(person.id);
            onClose?.();
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const surface = isDark ? '#1a1a2e' : '#ffffff';
    const surfaceAlt = isDark ? '#16162a' : '#f8fafc';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';
    const inputStyle = {
        width: '100%',
        padding: '8px 10px',
        background: surfaceAlt,
        color: textPrimary,
        border: `1px solid ${border}`,
        borderRadius: 8,
        fontSize: 13,
        outline: 'none',
        fontFamily: 'inherit',
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(15,23,42,0.5)',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: surface,
                    color: textPrimary,
                    borderRadius: 14,
                    width: '100%', maxWidth: 520,
                    maxHeight: '90vh', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                    border: `1px solid ${border}`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{
                    padding: '18px 22px',
                    borderBottom: `1px solid ${border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {isEdit ? 'Modifier la personne' : 'Nouvelle personne'}
                        </div>
                        <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                            {isEdit ? `Fiche de ${person.full_name}` : 'Ajouter un membre à la famille'}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'transparent', border: 'none', color: textSecondary, cursor: 'pointer', padding: 4, borderRadius: 6 }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: 22, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="Nom complet *" textSecondary={textSecondary}>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Prénom Nom"
                            style={inputStyle}
                            autoFocus
                        />
                    </Field>
                    <Field label="Rôle / relation" textSecondary={textSecondary}>
                        <input
                            type="text"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            placeholder="ex : Père, Conjointe, Enfant aîné"
                            style={inputStyle}
                        />
                    </Field>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <Field label="Date de naissance" textSecondary={textSecondary}>
                                <input
                                    type="date"
                                    value={birthDate}
                                    onChange={(e) => setBirthDate(e.target.value)}
                                    style={inputStyle}
                                />
                            </Field>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Field label="Nationalités" textSecondary={textSecondary}>
                                <input
                                    type="text"
                                    value={nationalities}
                                    onChange={(e) => setNationalities(e.target.value)}
                                    placeholder="FR, SN"
                                    style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace' }}
                                />
                            </Field>
                        </div>
                    </div>
                </div>

                <div style={{
                    padding: '14px 22px',
                    borderTop: `1px solid ${border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    background: surfaceAlt,
                }}>
                    <div>
                        {isEdit && (
                            <button
                                onClick={handleDelete}
                                disabled={saving}
                                style={{
                                    background: 'transparent', color: '#ef4444',
                                    border: `1px solid #ef444455`, borderRadius: 8,
                                    padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                }}
                                title="Retirer cette personne de la famille (soft delete)"
                            >
                                <Trash2 size={14} /> Retirer
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={onClose}
                            disabled={saving}
                            style={{
                                background: 'transparent', color: textSecondary,
                                border: `1px solid ${border}`, borderRadius: 8,
                                padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                            }}
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                background: '#1D4ED8', color: '#fff',
                                border: 'none', borderRadius: 8,
                                padding: '8px 16px', fontSize: 13, fontWeight: 500,
                                cursor: saving ? 'wait' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                                opacity: saving ? 0.65 : 1,
                            }}
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, children, textSecondary }) => (
    <div>
        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {label}
        </div>
        {children}
    </div>
);

export default PersonEditModal;
